<template>
  <section
    v-if="products.length > 0"
    :class="bemBlock"
    :data-context="tileContext"
  >
    <p v-if="heading" :class="bemElement('heading')">
      {{ heading }}
    </p>
    <div
      ref="swiper"
      :class="[bemElement('slider'), 'swiper', 'u-scrollbar-none']"
      itemtype="http://schema.org/SomeProducts"
    >
      <div class="swiper-wrapper">
        <div
          v-for="(item, index) in products"
          :key="`product-slider-${index}`"
          class="swiper-slide"
          :class="bemElement('item')"
        >
          <BaseProductTile
            :analytics="buildAnalyticsPropForProduct(index)"
            :context="tileContext"
            :keyid="`${item.product.id}-${index}`"
            :grid-index="index"
            :has-carousel="isBreakpointDesktop"
            :hide-tags="hideTileTags"
            :show-swatches="false"
            :product="item.product"
            :url="item.urls.product"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import getFrontConfig from '../../common/getFrontConfig';
import bemClassesMixin from '../mixins/bemClassesMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';
import BaseProductTile from './BaseProductTile.vue';
import EventBusConfig from '../config/eventBusConfig';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';
import analyticsMixin from '../mixins/analyticsMixin';

const {
  responsive: { bp_1025: bp1025 },
  vue: {
    BaseProductTile: { context },
  },
} = getFrontConfig();

const analyticsEventBus = getAnalyticsEventBus();

export default {
  name: 'BaseProductSlider',

  components: {
    BaseProductTile,
  },

  mixins: [analyticsMixin, bemClassesMixin, windowResizeMixin],

  props: {
    heading: {
      type: String,
      default: null,
    },
    products: {
      type: Array,
      default() {
        return [];
      },
    },
    tileContext: {
      type: String,
      default: context.pdpSlider,
    },
  },

  data: () => ({
    dataChangeBreakpoint: bp1025,
    isSwiperImported: false,
    isSwiperInitialized: false,
    resizeObserver: null,
    swiperInstance: null,
  }),

  computed: {
    hideTileTags() {
      return (
        this.tileContext === context.minicartSlider ||
        this.tileContext === context.searchModal
      );
    },
    locationForAnalytics() {
      /**
       * A key/value object to transform the prop 'conext' of BaseProductTile to the
       * location property of the payload of a ProductTileClickedEvent event.
       * @type {Object.<jsdocTypes.BaseProductTileContext, string>}
       */
      const tileContextToLocation = {
        [context.homeShopTheLook]: this.analyticsConst.location.homeShopTheLook,
        [context.minicartSlider]:
          this.analyticsConst.location.cartRecommendedProducts,
        [context.pdpRecommendedSlider]:
          this.analyticsConst.location.pdpRecommendedProducts,
        [context.pdpSlider]: this.analyticsConst.location.pdpCompleteTheLook,
        [context.searchModal]: this.analyticsConst.location.searchModal,
      };
      return tileContextToLocation[this.tileContext];
    },
    searchedTermForAnalytics() {
      let searchedTerm;
      if (this.tileContext === context.searchModal) {
        searchedTerm =
          analyticsEventBus.EVENTS.PAYLOADS.searchedTermNoText.type.featured;
      }
      return searchedTerm;
    },
  },

  watch: {
    products(newValue, oldValue) {
      if (newValue?.length !== oldValue?.length) {
        if (oldValue?.length > 0) {
          this.destroySwiper();
        }
        if (newValue?.length > 0) {
          this.initializeSwiper();
        }
        this.$eventBus.$emit(EventBusConfig.events.BaseProductSlider.updated, {
          products: this.products,
          productIds: this.products.map((item) => item.product.id),
          tileContext: this.tileContext,
        });
      }
    },
  },

  beforeCreate() {
    /*
    The component is prepared to load Swiper dinamically. However, it is is used
    whenever a carousel is shown in viewport. That condition is met when loading
    PLP, so will force to load the partial at the beginning.
    Although this partial seems useless, it will be cached by the browser and it
    will have less modifications than having code of the project.
    */
    import(/* webpackChunkName: "swiper.js" */ 'swiper').then(() => {
      this.isSwiperImported = true;
    });
  },

  beforeDestroy() {
    this.destroySwiper();
    this.resizeObserver?.disconnect();
  },

  mounted() {
    this.initializeSwiper();
  },

  methods: {
    buildAnalyticsPropForProduct(position) {
      return this.analyticsExtendProp({
        location: this.locationForAnalytics,
        position,
        searchedTerm: this.searchedTermForAnalytics,
      });
    },
    destroySwiper() {
      this.swiperInstance?.destroy();
      this.isSwiperInitialized = false;
    },
    updateImageSliderHeight() {
      const imageSliderHeight =
        this.$refs.swiper.querySelector('.image-container')?.offsetHeight || 0;
      this.$refs.swiper.style.setProperty(
        '--image-slider-height',
        `${imageSliderHeight}px`
      );
    },
    initializeSwiper() {
      if (this.isSwiperInitialized) return;
      if (this.products?.length <= 0) return;
      import(/* webpackChunkName: "swiper.js" */ 'swiper').then((module) => {
        const Swiper = module.default;
        this.swiperInstance = new Swiper(this.$refs.swiper, {
          loop: false,
          preventClicks: false,
          preventClicksPropagation: false,
          slidesPerView: 'auto',
          spaceBetween: 2,
          touchStartPreventDefault: false,
        });
        this.isSwiperInitialized = true;
        this.$eventBus.$emit(EventBusConfig.events.BaseProductSlider.ready, {
          products: this.products,
          productIds: this.products.map((item) => item.product.id),
          tileContext: this.tileContext,
        });
        if (this.hideTileTags) {
          this.updateImageSliderHeight();
          if (!this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(
              this.updateImageSliderHeight
            );
            this.resizeObserver.observe(this.$refs.swiper);
          }
        }
      });
    },
  },
};
</script>
